import { useOrderDetailContext } from '@euler/app/flows/order/components/OrderDetailProvider';
import { Center } from '@euler/components/Center';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useLayoutContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { safeMarkDevFlag } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

const kMargin = 0;
const kSizeRatio = 0.618;
const kUseFakeQrcode = safeMarkDevFlag(false);

export const VehicleSubscriptionQrcode = memo(
  ({
    style,
    onError,
    ...props
  }: {
    style?: StyleProp<ViewStyle>;
    onError?: () => void;
    onLoad?: (url: string, size: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const width = useLayoutContainerWidth();
    const size = Math.round(width);

    const { licensePlateNo, getQrcodeImageUrl } = useOrderDetailContext();

    const [qrcodeImageUrl, setQrcodeImageUrl] = useState<string>();

    const [error, setError] = useState<any>();
    const [loading, setLoading] = useState(true);

    const imageSize = width * kSizeRatio - rpx(kMargin) * 2;

    const onErrorLoad = useCallback(() => {
      setError('load qrcode image failed');
      setLoading(false);
      onError?.();
    }, [onError]);

    const onLoad = usePersistFn(() => {
      setLoading(false);
      props.onLoad?.(qrcodeImageUrl!, imageSize);
    });

    useEffect(() => {
      void getQrcodeImageUrl?.({
        lpn: licensePlateNo,
        size,
        __fake__: kUseFakeQrcode,
      }).then(setQrcodeImageUrl);
    }, [getQrcodeImageUrl, licensePlateNo, size]);

    if (error || !qrcodeImageUrl) {
      return null;
    }

    return (
      <View
        style={style}
        css={`
          align-items: center;
        `}
      >
        <Img
          uri={qrcodeImageUrl}
          css={`
            width: ${imageSize}px;
            height: ${imageSize}px;
          `}
          onLoad={onLoad}
          onError={onErrorLoad}
        />
        {loading && (
          <Center style={StyleSheet.absoluteFill}>
            <ActivityIndicator />
          </Center>
        )}
      </View>
    );
  },
);
